import { useState, useEffect, useCallback } from 'react';
import { Link, router } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import SearchableSelect from '@/Components/SearchableSelect';
import Pagination from '@/Components/Pagination';

export default function DestinationsIndex({ destinations, countries, filters }) {
    const [search, setSearch] = useState(filters.search || '');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [destinationToDelete, setDestinationToDelete] = useState(null);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (search !== (filters.search || '')) {
                router.get('/destinations', { ...filters, search }, {
                    preserveState: true,
                    replace: true,
                });
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [search]);

    const handleFilter = (key, value) => {
        router.get('/destinations', { ...filters, search, [key]: value }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleToggleStatus = (destination) => {
        router.patch(`/destinations/${destination.id}/toggle-status`, {}, {
            preserveScroll: true,
        });
    };

    const handleToggleFeatured = (destination) => {
        router.patch(`/destinations/${destination.id}/toggle-featured`, {}, {
            preserveScroll: true,
        });
    };

    const handleDelete = () => {
        router.delete(`/destinations/${destinationToDelete.id}`, {
            onSuccess: () => {
                setShowDeleteModal(false);
                setDestinationToDelete(null);
            },
        });
    };

    const handleClearFilters = () => {
        setSearch('');
        router.get('/destinations');
    };

    return (
        <AppLayout title="Destinations">
            <PageHeader
                title="Destinations"
                subtitle={`${destinations.total.toLocaleString()} destinations`}
                breadcrumbs={[
                    { label: 'Travel' },
                    { label: 'Destinations' },
                ]}
                actions={
                    <Link href="/destinations/create" className="btn btn-primary">
                        <i className="bi bi-plus-lg"></i>
                        <span>Add Destination</span>
                    </Link>
                }
            />

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white">
                                    <i className="bi bi-search"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Type to search destinations..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        type="button"
                                        onClick={() => setSearch('')}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small text-muted mb-1">Country</label>
                            <SearchableSelect
                                options={countries}
                                value={filters.country || ''}
                                onChange={(value) => handleFilter('country', value)}
                                placeholder="All Countries"
                                searchPlaceholder="Search countries..."
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Status</label>
                            <select
                                className="form-select"
                                value={filters.status || ''}
                                onChange={(e) => handleFilter('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Per Page</label>
                            <select
                                className="form-select"
                                value={destinations.per_page}
                                onChange={(e) => handleFilter('per_page', e.target.value)}
                            >
                                <option value="15">15</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-outline-secondary w-100"
                                title="Clear all filters"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Destinations Table */}
            <div className="card">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th>Destination</th>
                                <th>Country</th>
                                <th className="text-center">Featured</th>
                                <th className="text-center">Status</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {destinations.data.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="text-muted">
                                            <i className="bi bi-geo-alt fs-1 d-block mb-3"></i>
                                            No destinations found
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                destinations.data.map((destination, index) => (
                                    <tr key={destination.id}>
                                        <td className="text-muted">
                                            {(destinations.current_page - 1) * destinations.per_page + index + 1}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                {destination.image ? (
                                                    <img
                                                        src={`/storage/${destination.image}`}
                                                        alt={destination.name}
                                                        className="rounded"
                                                        style={{ width: 40, height: 40, objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="avatar avatar-sm"
                                                        style={{
                                                            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                                                            color: 'white',
                                                            width: 40,
                                                            height: 40,
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        {destination.name.substring(0, 2).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="fw-semibold">{destination.name}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge bg-light text-dark">
                                                {destination.country?.name}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                className="btn btn-sm btn-link p-0"
                                                onClick={() => handleToggleFeatured(destination)}
                                                title={destination.is_featured ? 'Remove from featured' : 'Add to featured'}
                                            >
                                                <i className={`bi ${destination.is_featured ? 'bi-star-fill text-warning' : 'bi-star text-muted'} fs-5`}></i>
                                            </button>
                                        </td>
                                        <td className="text-center">
                                            <span className={`badge ${destination.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                                {destination.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            <div className="btn-group">
                                                <Link
                                                    href={`/destinations/${destination.id}/edit`}
                                                    className="btn btn-sm btn-outline-secondary"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Link>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => handleToggleStatus(destination)}
                                                    title={destination.is_active ? 'Deactivate' : 'Activate'}
                                                >
                                                    <i className={`bi ${destination.is_active ? 'bi-toggle-on text-success' : 'bi-toggle-off'}`}></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => {
                                                        setDestinationToDelete(destination);
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <Pagination
                    data={destinations}
                    showPerPage
                    onPerPageChange={(value) => handleFilter('per_page', value)}
                />
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Delete Destination</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowDeleteModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p>Are you sure you want to delete <strong>{destinationToDelete?.name}</strong>?</p>
                                    <p className="text-muted small mb-0">This action cannot be undone.</p>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </AppLayout>
    );
}
